import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useI18n } from '../i18n/index.ts';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  /** 'drawer' slides from the side (bottom sheet on phones); 'modal' is centred. */
  variant?: 'drawer' | 'modal';
  /** When false, Esc and backdrop do not close it (e.g. session expired). */
  dismissible?: boolean;
  children: ReactNode;
  testId?: string;
}

/**
 * Native <dialog> with showModal(): the rest of the page becomes inert (focus stays inside),
 * Esc closes when dismissible, and focus returns to the opener on close.
 */
export function Dialog({ open, title, onClose, variant = 'drawer', dismissible = true, children, testId }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const titleId = useId();
  const { t } = useI18n();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement;
      d.showModal();
    } else if (!open && d.open) {
      d.close();
      if (opener.current instanceof HTMLElement) opener.current.focus();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={`dialog dialog-${variant}`}
      aria-labelledby={titleId}
      data-testid={testId}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(e) => {
        if (dismissible && e.target === ref.current) onClose();
      }}
    >
      <div className="dialog-body">
        <header className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          {dismissible && (
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t('nav.close')}>
              ✕
            </button>
          )}
        </header>
        {/* Focusable so keyboard users can scroll long content (rules, evidence) with arrow keys. */}
        <div className="dialog-content" tabIndex={0} role="region" aria-labelledby={titleId}>
          {children}
        </div>
      </div>
    </dialog>
  );
}
